import { Copy, ExternalLink, Trash2 } from 'lucide-react'
import { useEffect } from 'react'
import { createPortal } from 'react-dom'
import { Column } from 'src/shared/components/Semantic/all'
import { cn } from 'src/shared/components/ui/utils'
import { useLocalization } from 'src/shared/hooks/useLocalization'
import type { CanvasContextTarget } from '../types'

export type CanvasContextMenuItem = {
  label: string
  icon: React.ReactNode
  onClick: () => void
  destructive?: boolean
}

export const CanvasContextMenu = ({
  x,
  y,
  target,
  selectionCount = 1,
  onPaste,
  onDuplicate,
  onDelete,
  onOpenLink,
  onClose,
}: {
  x: number
  y: number
  target: CanvasContextTarget
  selectionCount?: number
  onPaste?: () => void
  onDuplicate?: () => void
  onDelete?: () => void
  onOpenLink?: () => void
  onClose: () => void
}) => {
  const { t } = useLocalization()
  const multi = selectionCount > 1

  useEffect(() => {
    const close = () => onClose()
    window.addEventListener('pointerdown', close)
    window.addEventListener('scroll', close, true)
    window.addEventListener('keydown', close)
    return () => {
      window.removeEventListener('pointerdown', close)
      window.removeEventListener('scroll', close, true)
      window.removeEventListener('keydown', close)
    }
  }, [onClose])

  const items: CanvasContextMenuItem[] = []

  if (target.kind === 'canvas' && onPaste) {
    items.push({
      label: t('Paste', 'Klistra in'),
      icon: <Copy className="h-3.5 w-3.5" />,
      onClick: onPaste,
    })
  }

  if (onDuplicate && (multi || target.kind !== 'canvas')) {
    items.push({
      label: multi
        ? t(`Duplicate ${selectionCount}`, `Duplicera ${selectionCount}`)
        : t('Duplicate', 'Duplicera'),
      icon: <Copy className="h-3.5 w-3.5" />,
      onClick: onDuplicate,
    })
  }

  if (target.kind === 'link' && onOpenLink && !multi) {
    items.push({
      label: t('Open link', 'Öppna länk'),
      icon: <ExternalLink className="h-3.5 w-3.5" />,
      onClick: onOpenLink,
    })
  }

  if (target.kind !== 'canvas' && onDelete) {
    items.push({
      label: multi
        ? t(`Delete ${selectionCount}`, `Ta bort ${selectionCount}`)
        : t('Delete', 'Ta bort'),
      icon: <Trash2 className="h-3.5 w-3.5" />,
      onClick: onDelete,
      destructive: true,
    })
  }

  if (!items.length) return null

  return (
    <>
      {createPortal(
        <Column
          className="fixed z-[200] min-w-[168px] gap-0.5 rounded-lg border border-gray-200 bg-white p-1 shadow-xl"
          style={{ left: x, top: y }}
          onPointerDown={(e) => e.stopPropagation()}
        >
          {items.map((item) => (
            <button
              key={item.label}
              type="button"
              className={cn(
                'flex w-full items-center justify-start gap-2 rounded-md px-2.5 py-1.5 text-left text-xs transition-colors',
                item.destructive
                  ? 'text-red-600 hover:bg-red-50'
                  : 'text-gray-700 hover:bg-gray-100'
              )}
              onClick={() => {
                item.onClick()
                onClose()
              }}
            >
              {item.icon}
              {item.label}
            </button>
          ))}
        </Column>,
        document.body
      )}
    </>
  )
}
